const _ = require("lodash");
const LoadState = require("./util/LoadState");
const GradeChapter = require("../../../shared/GradeChapter");
const ChapterSection = require("../../../shared/ChapterSection");
const BlockStyle = require("./BlockStyle");
const OpsBlock = require("./OpsBlock");
const ResultManager = require("../../../shared/ResultManager");

const {FLAGS} = require("../util/GlobalFlags");

const {
	atDemoData,
	atPage,
	atUser,
	atLibraryChapter,
	atLibraryPages,
	atTheaterChapter,
	atTheaterSet,
	atChapter,
	atBlock,
	atLabType,
	atLabBlock,
	atChapterByBlockID,
	atCurrBlock,
	atCurrLabBlock,
	atCurrResult,
} = require("./StateCursors");

/**
 * A file for getting derived data from vault state. However the StateCursors
 * and Ops files also serve this purpose in more specific ways, so this file is
 * now a grab bag for derived data functions that don't fit anywhere else.
 * Note that this file can (and does) depend on Ops files, but Ops files should
 * *never* depend on this file.
 *
 * Here are some guidelines for determining which file to add a function to:
 * - If your derived data is merely computing a key path in the vault state
 *   and returning the value there, you should add a function to StateCursors
 *   instead, and it should return a cursor at that path.
 * - If your function only makes use of one piece of the vault state, e.g. the
 *   page, add it to the appropriate Ops file.
 * - If your function needs the entire state object and it returns something
 *   more complicated than a cursor into the state, this file is the right
 *   place. That's probably the only case in which you add to this file.
 */

const getUserActivity = function(state) {
	return state.user.currentActivity;
};

const isLoggedIn = function(state) {
	return !!atUser(state).getIn("userID");
};

const getBadChapter = function() {
	return LoadState.loadError({}, new Error("Tried to fetch null chapter ID"));
};

const getBadLabType = function() {
	return LoadState.loadError({}, new Error("Tried to fetch null lab type ID"));
};

const getLibraryPagesForChapter = function(state, chapterID) {
	const lpages = atLibraryPages(state).get();
	return lpages && lpages.chapterID === chapterID ? lpages : null;
};

const isViewingLibrary = function(state) {
	const page = atPage(state).get();
	return (
		page &&
		(page.name === "library.chapter" ||
			(page.name === "trainer.problem" && page.showBook) ||
			(page.name === "trainer.history" && page.showBook) ||
			(page.name === "lab.problem" && page.showBook) ||
			(page.name === "theater.set" && page.showBook) ||
			(page.name === "trainer.chapter" && page.showBook))
	);
};

const isViewingTheater = function(state) {
	const page = atPage(state).get();
	return (
		page &&
		(page.name === "theater.set" ||
			(page.name === "trainer.problem" && page.showVideo) ||
			(page.name === "trainer.history" && page.showVideo) ||
			(page.name === "lab.problem" && page.showVideo))
	);
};

const getLibraryCurrent = function(state) {
	if (!isViewingLibrary(state)) return null;

	const page = atPage(state).get();
	const chapterID = GradeChapter.toChapterID(
		page.gradeNumber,
		page.chapterIndex
	);
	const lpages = getLibraryPagesForChapter(state, chapterID);
	if (!lpages) return null;

	const pageNumber = lpages.currPageNumber || null;
	const sectionID =
		pageNumber &&
		ChapterSection.getSectionIDFromChapterPage(
			chapterID,
			pageNumber,
			!!page.demo
		);
	return {
		chapterID,
		sectionID,
		pageNumber,
	};
};

const getTheaterCurrent = function(state) {
	if (!isViewingTheater(state)) return null;

	const page = atPage(state).get();
	let videoSetID;
	if (page.name === "theater.set") {
		const chapterID = GradeChapter.toChapterID(
			page.gradeNumber,
			page.chapterIndex
		);
		const chapterBlocks = atTheaterChapter(state, chapterID).getIn("blocks");
		const block = _.find(chapterBlocks, {blockID: page.blockID});
		videoSetID = block && block.videoSetID;
	} else if (page.name === "trainer.problem") {
		videoSetID = atCurrBlock(state).getIn(["model", "videoSetID"]);
	} else if (page.name === "lab.problem") {
		videoSetID = atCurrLabBlock(state).getIn(["model", "videoSetID"]);
	}

	const set = videoSetID && atTheaterSet(state, videoSetID).get();
	if (!set) {
		return null;
	}
	const videoID = _.get(set, ["model", "videoIDs", page.videoIndex || 0]);
	return {
		videoSetID,
		videoID,
	};
};

/**
 * Determines total count of all pages in chapterID that are read/completed
 *
 * @param {number} chapterModel - chapterModel
 * @param {boolean} demo - whether we're in demo
 *
 * @return {number} - total number of pages in the chapter
 */
const libraryChapterGetTotalPageCount = ({chapterID}, demo) => {
	const chapterPages = ChapterSection.getChapterPages(chapterID, demo);
	return chapterPages[1] - chapterPages[0] + 1;
};

/**
 * Determines number of pages in chapterID that are read/completed
 *
 * @param {Object} chapterModel - chapterModel
 * @param {boolean} demo - whether we're in demo
 *
 * @return {number} - number of pages read in the chapter
 */
const libraryChapterGetNumPagesRead = (chapterModel, demo) => {
	const {chapterID, pagesRead, sectionsMarkedDone = []} = chapterModel;

	const sections = ChapterSection.getChapterSections(chapterID, demo);
	if (!sections) {
		// We shouldn't get in here, but juuuuust in case...
		return pagesRead?.length || 0;
	}
	if (_.intersection(sectionsMarkedDone, sections).length === sections.length) {
		return libraryChapterGetTotalPageCount(chapterModel, demo);
	}
	const pagesReadPerSection = sections.map(sectionID => {
		const sectionPages = ChapterSection.getChapterSectionPages(sectionID);
		const pages = _.range(sectionPages[0], sectionPages[1] + 1);
		const sectionPagesRead = pages.filter(
			page => pagesRead.includes(page) || sectionsMarkedDone.includes(sectionID)
		);
		return sectionPagesRead.length;
	});
	const totalPagesRead = pagesReadPerSection.reduce((a, b) => a + b, 0);
	return totalPagesRead;
};

/**
 * Determines percentage of pages in chapterID that are read/completed
 *
 * @param {Object} chapterModel - chapterModel
 * @param {boolean} demo - whether we're in demo
 *
 * @return {number} - percentage (between 0 and 1) of pages read in the chapter
 */
const percentLibraryChapterRead = function(chapterModel, demo) {
	const chapterPages = ChapterSection.getChapterPages(
		chapterModel.chapterID,
		demo
	);
	if (!chapterPages) {
		return 0;
	}
	const numPagesRead = libraryChapterGetNumPagesRead(chapterModel, demo);
	const totalPages = libraryChapterGetTotalPageCount(chapterModel, demo);
	return numPagesRead / totalPages;
};

const getInactiveLibraryChapters = function() {
	return FLAGS.accessUnfinishedChapters
		? []
		: GradeChapter.getInactiveLibraryChapters();
};

const getInactiveTheaterChapters = function() {
	return GradeChapter.getInactiveTheaterChapters();
};

const getActiveGrades = function() {
	return FLAGS.accessUnfinishedGrades
		? GradeChapter.getAllGrades()
		: GradeChapter.getActiveGrades();
};

const getBlockStarsForChapter = function(state, chapterID) {
	const chapter = atChapter(state, chapterID).get();
	if (!chapter || !chapter.model) {
		return null;
	}
	return chapter.model.blockIDs.map(blockID => {
		return atBlock(state, blockID).getIn("model.starsObtained", 0);
	});
};

const getBadBlock = function() {
	return LoadState.loadError(
		{},
		new Error("Tried to fetch null trainer block ID")
	);
};

const getBadLabBlock = function() {
	return LoadState.loadError({}, new Error("Tried to fetch null lab block ID"));
};

const getUnlockedStatusForNextBlock = function(state, blockID) {
	const chapterBlocks = atChapterByBlockID(state, blockID).getIn(
		"model.blockIDs"
	);
	if (!chapterBlocks) {
		return false;
	}
	const index = chapterBlocks.indexOf(blockID);
	if (index < 0 || index === chapterBlocks.length - 1) {
		return false;
	}
	return atBlock(state, chapterBlocks[index + 1]).getIn("model.unlocked");
};

const getBlockStarsForBlock = function(state, blockID, myStars) {
	const block = atBlock(state, blockID).get();
	const chapter = atChapterByBlockID(state, blockID).get();
	if (!block || !block.model || !chapter) {
		return null;
	}
	const blockStars = getBlockStarsForChapter(state, chapter.chapterID);
	if (myStars !== undefined) {
		blockStars[block.model.blockIndex] = myStars;
	}
	return blockStars;
};

const getCurrentModalStyle = function(state) {
	const block = atCurrBlock(state).get();
	const result = atCurrResult(state).get();
	const page = atPage(state).get();
	return BlockStyle.getType(_.extend({block, result}, page)) || "default";
};

// Main lesson blocks for the chapter only (not test/review/challenge — use getChapterAuxBlocks).
const getChapterMainBlocks = function(state, chapterID) {
	return atChapter(state, chapterID)
		.getIn("model.blockIDs", [])
		.map(id => atBlock(state, id).get());
};

const getLabTypeBlocks = function(state, typeID) {
	return atLabType(state, typeID)
		.getIn("model.blockIDs", [])
		.map(id => atLabBlock(state, id).get())
		.filter(
			b =>
				FLAGS.accessUnderConstructionLabBlocks ||
				(!_.get(b, "model.underConstruction") && _.get(b, "model.releasedAt"))
		);
};

const getChapterAuxBlockNames = function() {
	const auxBlocks = [];
	// For reference when later switching review back on: [T1723]
	if (FLAGS.accessReview) {
		auxBlocks.push("review");
	}
	auxBlocks.push("test");
	if (FLAGS.accessChallenge) {
		auxBlocks.push("challenge");
	}

	return auxBlocks;
};

// { test, review?, challenge? } — full vault block objects. Chapter test passed when test.model.starsObtained > 0.
const getChapterAuxBlocks = function(state, chapterID) {
	const chapterModel = atChapter(state, chapterID).getIn("model");
	if (!chapterModel) {
		return {};
	} else {
		const auxBlockInfo = {};
		_.each(getChapterAuxBlockNames(), name => {
			auxBlockInfo[name] = atBlock(state, chapterModel[name + "BlockID"]).get();
		});
		return auxBlockInfo;
	}
};

const areChapterAuxBlocksUnlocked = function(state, chapterID) {
	const chapterAuxBlocks = getChapterAuxBlocks(state, chapterID);
	const unlockValues = _.mapValues(
		chapterAuxBlocks,
		b => b && b.model && b.model.unlocked
	);
	if (chapterAuxBlocks.test) {
		unlockValues.suppressUnlockFromProgress = OpsBlock.suppressUnlockFromProgress(
			chapterAuxBlocks.test
		);
	}
	return unlockValues;
};

const getAllChapterBlockIDs = function(state, chapterID) {
	const chapterModel = atChapter(state, chapterID).getIn("model");
	if (!chapterModel) {
		return [];
	} else {
		return _.concat(chapterModel.blockIDs, [
			chapterModel.reviewBlockID,
			chapterModel.testBlockID,
			chapterModel.challengeBlockID,
		]).filter(Boolean);
	}
};

const areChapterBlocksUnlocked = function(state, chapterID) {
	const chapterBlocks = getChapterMainBlocks(state, chapterID);
	return chapterBlocks.map(b => b.model.unlocked);
};

const getUnfinishedChapterBlocks = function(state, chapterID) {
	const chapterBlocks = getChapterMainBlocks(state, chapterID);
	return chapterBlocks.filter(b => b.model.unfinished);
};

const getLabTypeBlockIDs = function(state, typeID) {
	const labBlocks = getLabTypeBlocks(state, typeID);
	return labBlocks.map(b => b.model.blockID);
};

const areLabTypeBlocksUnlocked = function(state, typeID) {
	const labBlocks = getLabTypeBlocks(state, typeID);
	return labBlocks.map(b => b.model.unlocked);
};

const getLabTypeBlocksCelestialIDs = function(state, typeID) {
	const labBlocks = getLabTypeBlocks(state, typeID);
	return labBlocks.map(b => b.model.celestialID);
};

const getSharedProblemModel = function(state, problemID) {
	return state.sharedProblemModels[problemID] || null;
};

const getInstructionsForBlock = function(state, blockID) {
	return atBlock(state, blockID).getIn("model.instructions");
};

const checkResultForProblems = function(result) {
	if (!result || !result.resultType) {
		return false;
	}
	return !!result.problems.length;
};

const convertTime = function(time) {
	return Math.ceil(time / 60);
};

//Gets time spent in minutes rounded up.
const getBlockTimeSpent = function(block) {
	let time = parseFloat(block.model.totalTimeSpent);
	if (block.currentResult) {
		time += block.currentResult.timeSpent / 1000;
	}
	return convertTime(time);
};

const getDemoLibraryChapters = function(state) {
	const chapters = {load: "done", loadError: null};

	_.forEach(atDemoData(state).get().libraryChapterData, (chapterData, cid) => {
		let chapter = atLibraryChapter(state, cid).get();
		if (!chapter.model) {
			chapter = chapterData;
		}
		chapters[cid] = chapter;
	});

	return chapters;
};

const getSectionBlockMapForChapter = function(state, chapterID) {
	const sectionMap = {};

	// get blocks for chapter
	const blocks = getChapterMainBlocks(state, chapterID);

	// get sections and set in sectionMap
	const sectionIDs = [0].concat(ChapterSection.getChapterSections(chapterID));
	_.each(sectionIDs, sid => (sectionMap[sid] = []));

	// current section
	let currentSectionIndex = 0;

	blocks.forEach(block => {
		const bookSectionID = _.get(block, "model.bookSectionID");
		if (
			bookSectionID &&
			_.isEmpty(sectionMap[bookSectionID]) &&
			_.includes(sectionIDs, bookSectionID) &&
			_.indexOf(sectionIDs, bookSectionID) > currentSectionIndex
		) {
			currentSectionIndex = _.indexOf(sectionIDs, bookSectionID);
		}
		sectionMap[sectionIDs[currentSectionIndex]].push(block.model);
	});

	return sectionMap;
};

const getBlockInChapter = function(state, chapter, blockIndex) {
	const chapterModel = (chapter && chapter.model) || {};
	if (!chapterModel || !chapterModel.blockIDs) {
		return null;
	}
	const blockIDs = chapterModel.blockIDs || [];

	const blockID = blockIDs[blockIndex];
	return atBlock(state, blockID).get();
};

const getNextBlockInChapter = function(state, chapter, block) {
	const chapterModel = (chapter && chapter.model) || {};
	const {blockID} = block;
	const blockModel = (block && block.model) || {};
	if (!blockModel || !chapterModel || !chapterModel.blockIDs) {
		return null;
	}
	const blockIDs = chapterModel.blockIDs || [];
	const {blockIndex} = blockModel;

	// If the current block is the last block in the chapter, the test is next
	// otherwise it will be the blockID of blockIDs[currentBlockIndex + 1]
	const nextBlockID =
		_.last(blockIDs) === blockID
			? _.get(chapter.model, "testBlockID")
			: blockIDs[blockIndex + 1];

	return atBlock(state, nextBlockID).get();
};

const getNextCoreBlockInChapter = function(state, chapter, block) {
	const chapterModel = (chapter && chapter.model) || {};
	const blockModel = (block && block.model) || {};
	if (!blockModel || !chapterModel || !chapterModel.blockIDs) {
		return null;
	}
	const blockIDs = chapterModel.blockIDs || [];

	// Look forward from this current block to try to find the next core block.
	for (
		let nextBlockIndex = blockModel.blockIndex + 1;
		nextBlockIndex < blockIDs.length;
		nextBlockIndex++
	) {
		const nextBlockID = blockIDs[nextBlockIndex];
		const nextBlock = atBlock(state, nextBlockID).get();
		if (OpsBlock.isCoreLesson(nextBlock)) {
			return nextBlock;
		}
	}
	// If we didn't find one, either we started at the end or the remaining blocks must be non-core blocks, so let's return the test.
	return atBlock(state, _.get(chapter.model, "testBlockID")).get();
};

const getLastCompletedHistoryResult = function(block) {
	if (!block || !block.history || block.history.load !== "done") {
		return null;
	}
	const results = block.history.allResults;
	if (!results || !results.length) {
		return null;
	}
	const isDisplayable = function(r) {
		return (
			(r.resultType === "test" && r.hasQuit) ||
			r.isComplete ||
			r.hasIntervened ||
			ResultManager(r).neverResumeResult
		);
	};
	const hasSummaryStars = function(r) {
		return (r.starsObtained || 0) > 0;
	};
	const id = block.history.lastCompletedResultID;
	if (id) {
		const match = _.find(results, r => r.resultID === id);
		if (match && isDisplayable(match)) {
			if (!hasSummaryStars(match)) {
				return null;
			}
			return match;
		}
	}
	return _.find(results, r => isDisplayable(r) && hasSummaryStars(r));
};

module.exports = {
	getUserActivity,
	isLoggedIn,
	getBadChapter,
	getBadLabType,
	getLibraryPagesForChapter,
	isViewingLibrary,
	isViewingTheater,
	getLibraryCurrent,
	getTheaterCurrent,
	libraryChapterGetTotalPageCount,
	libraryChapterGetNumPagesRead,
	percentLibraryChapterRead,
	getBlockStarsForChapter,
	getBadBlock,
	getBadLabBlock,
	getBlockStarsForBlock,
	getCurrentModalStyle,
	getChapterMainBlocks,
	getChapterAuxBlocks,
	getChapterAuxBlockNames,
	areChapterAuxBlocksUnlocked,
	getAllChapterBlockIDs,
	areChapterBlocksUnlocked,
	getUnfinishedChapterBlocks,
	getLabTypeBlockIDs,
	areLabTypeBlocksUnlocked,
	getLabTypeBlocksCelestialIDs,
	getSharedProblemModel,
	getInstructionsForBlock,
	getUnlockedStatusForNextBlock,
	checkResultForProblems,
	convertTime,
	getBlockTimeSpent,
	getInactiveLibraryChapters,
	getInactiveTheaterChapters,
	getActiveGrades,
	getDemoLibraryChapters,
	getLabTypeBlocks,
	getSectionBlockMapForChapter,
	getBlockInChapter,
	getNextBlockInChapter,
	getNextCoreBlockInChapter,
	getLastCompletedHistoryResult,
};
